import { Capacitor } from '@capacitor/core';

/**
 * Zentrale Weiche für die native iOS-Shell (Capacitor, Remote-URL-Modus).
 *
 * Die App lädt die Prod-Domain im WKWebView; dieser Code läuft also im
 * normalen Web-Bundle und erkennt zur Laufzeit, ob die Capacitor-Bridge
 * injiziert ist. ALLE nativen Sonderwege gehören hinter isNativeApp() —
 * das Web-Verhalten muss byte-identisch bleiben.
 *
 * Drift-Regel: Plugin-APIs nur nutzen, wenn die verteilte Shell sie kennt
 * (Capacitor.isPluginAvailable), sonst bricht ein Web-Deploy alte Shells.
 */

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/** Blob → base64 (ohne data:-Prefix), für Filesystem.writeFile. */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Datei „herunterladen": Web = klassischer Anchor-Download; nativ =
 * in den Cache schreiben + iOS-Share-Sheet (WKWebView hat keinen
 * Download-Handler — Anchor-Klicks verpuffen dort stumm).
 */
export async function saveAndShareFile(opts: {
  filename: string;
  mimeType: string;
  content: Blob | string;
}): Promise<void> {
  const blob =
    typeof opts.content === 'string'
      ? new Blob([opts.content], { type: opts.mimeType })
      : opts.content;

  if (isNativeApp()) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);
    const written = await Filesystem.writeFile({
      path: opts.filename,
      data: await blobToBase64(blob),
      directory: Directory.Cache,
    });
    await Share.share({ url: written.uri });
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = opts.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Externen Link öffnen: Web = neuer Tab; nativ = System-Browser (Safari).
 * Externe Ziele (Google/Outlook Calendar …) gehören nicht in den
 * Backoffice-WebView — dort fehlen Logins und ein „Zurück".
 */
export async function openExternal(url: string): Promise<void> {
  if (isNativeApp()) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
    return;
  }
  window.open(url, '_blank', 'noopener');
}
